import { useContext, useMemo } from 'react'
import { useThemeUI } from 'theme-ui'
import getSizes from './get-sizes'
import {
  isChildOfContainerContext,
  columnSizeContext,
  useContainerSize,
} from '@uswitch/ev-core/react'

const decimalToPercentage = (num) => {
  const percentageMultiplier = 100
  return num * percentageMultiplier
}

// To manage possible decimal values e.g 33.3333...
export const convertViewportWidthToNumber = (vw) => {
  const vwPattern = /(vw)/
  return Number(vw.replace(vwPattern, ''))
}

// Calculates the largest an image can be based on the column bounds for breakpoints
export const calculateMaxImageSizeWithinColumnBounds = (
  columnSizes = [],
  theme,
  isInsideContainer,
  containerSize,
) => {
  return columnSizes.map(({ currentSpan, colValue }, i) => {
    const columnProportion = currentSpan / colValue

    if (isInsideContainer) {
      const currentBreakpoint = theme.breakpoints[i]
        ? Number(theme.breakpoints[i]?.replace(/[^0-9]/g, ''))
        : containerSize

      const imageMaxWidth = Math.ceil(currentBreakpoint * columnProportion)

      if (currentBreakpoint < containerSize) {
        return `${decimalToPercentage(columnProportion)}vw`
      }

      return `${imageMaxWidth}px`
    }

    return `${decimalToPercentage(columnProportion)}vw`
  })
}

// srcSet utilities

export const findLargestPossibleImageSizeFromSizes = (
  sizes,
  devicePixelRatio = 1,
  isInContainer,
  containerSize,
  upperLimitViewportDefault = 2800,
) => {
  const removeBetweenBracketsAndNonNumbersPattern = / *\([^)]*\) *|[a-z]/g
  const insideBracketRegexGlobal = / *\([^)]*\)/g
  const nonDigitRegexGlobal = /\D/g
  const vwPattern = /(vw)/
  const pxPattern = /(px)/
  const pxPatternGlobal = /(px)/g
  const sizeGroups = sizes.split(',')

  const maxWidthsAtBreakpoint = sizeGroups.map((group) => {
    const breakpointPresent = !!group.match(insideBracketRegexGlobal)

    if (!breakpointPresent) {
      // If its px we return
      if (pxPattern.test(group)) {
        return group.replace(nonDigitRegexGlobal, '')
      }

      if (isInContainer) {
        if (vwPattern.test(group)) {
          const vwMultiplier = convertViewportWidthToNumber(group)
          return Math.ceil((containerSize * vwMultiplier) / 100)
        }
        return containerSize
      }

      if (vwPattern.test(group)) {
        const vwMultiplier = convertViewportWidthToNumber(group)
        return Math.ceil((upperLimitViewportDefault * vwMultiplier) / 100)
      }

      return upperLimitViewportDefault
    }

    const breakpointIsPx = pxPattern.test(group.match(insideBracketRegexGlobal))
    const breakpointAndSourceSizeArePx = breakpointIsPx && group.match(pxPatternGlobal).length === 2
    // return the sourceSize as number
    if (breakpointAndSourceSizeArePx) {
      const maximumImageSize = group.replace(removeBetweenBracketsAndNonNumbersPattern, '')
      return maximumImageSize
    }

    if (breakpointIsPx) {
      // Convert vw max value
      if (vwPattern.test(sizes)) {
        return convertVwSizeWithBreakpointToMaxSize(group)
      }
    }

    // Fallback
    if (isInContainer) {
      return containerSize
    }

    return upperLimitViewportDefault
  })
  return Math.max(...maxWidthsAtBreakpoint) * devicePixelRatio
}

const convertVwSizeWithBreakpointToMaxSize = (sizeGroup) => {
  const insideBracketRegex = / *\([^)]*\)/g
  const nonDigitRegexGlobal = /\D/g

  const insideBracketText = sizeGroup.match(insideBracketRegex)
  const breakpointValue = insideBracketText[0].replace(nonDigitRegexGlobal, '')

  const outsideBracketText = sizeGroup.replace(insideBracketText, '')
  const vwMultiplier = convertViewportWidthToNumber(outsideBracketText)
  const outsideBracketPixelValue = Math.ceil((vwMultiplier * breakpointValue) / 100)
  return outsideBracketPixelValue
}

export const useImageOptimisation = ({ height, width, sizes, devicePixelRatios }) => {
  const { theme } = useThemeUI()

  const widthAndHeightProvided = !!height && !!width
  const maxDevicePixelRatio = Math.max(...devicePixelRatios)

  const columnSizes = useContext(columnSizeContext)
  const canSetColumnSize = columnSizes?.length > 0 && !widthAndHeightProvided

  const isInsideContainer = useContext(isChildOfContainerContext)
  const canSetContainerSize = isInsideContainer && !widthAndHeightProvided
  const containerSize = useContainerSize()

  /* 
  Order of precedence for the sizes attribute:  
  1. If columns and their widths are present the column width is the maximum
     an image can be and this is used
  2. If sizes is passed through to the imgix component this is used
  3. If the item is in a container we use the containers width
  4. If the width is provided to the image we set sizes to the width value
  5. Default value of 100vw  
  */

  const maxImageSizesWithinColumnBounds = useMemo(() => {
    if (canSetColumnSize) {
      return calculateMaxImageSizeWithinColumnBounds(
        columnSizes,
        theme,
        isInsideContainer,
        containerSize,
      )
    }
  }, [canSetColumnSize, columnSizes, theme, isInsideContainer, containerSize])

  let imageSizes

  if (canSetColumnSize) {
    imageSizes = getSizes(maxImageSizesWithinColumnBounds, theme.breakpoints)
  } else if (sizes) {
    imageSizes = sizes
  } else if (canSetContainerSize) {
    imageSizes = `(max-width: ${containerSize}px) 100vw, ${containerSize}px`
  } else if (width) {
    imageSizes = `${width}px`
  } else {
    imageSizes = '100vw'
  }

  /* 
  Order of precedence for the max width source set: 
  1. If both width and height are specified, use the width * device pixel ratio
    e.g 1000 width at 2DPR will use a srcset up to 2000px
  2. Use function to return a maximum possible width based on sizes values generated before
  */
  const largestPossibleImageFromSizes = useMemo(() => {
    if (!widthAndHeightProvided) {
      return findLargestPossibleImageSizeFromSizes(
        imageSizes,
        maxDevicePixelRatio,
        isInsideContainer,
        containerSize,
      )
    }
  }, [widthAndHeightProvided, imageSizes, maxDevicePixelRatio, isInsideContainer, containerSize])

  const maxWidth = widthAndHeightProvided
    ? width * maxDevicePixelRatio
    : largestPossibleImageFromSizes

  return { imageSizes, maxWidth }
}
